import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useConfirmDialog } from '@/components/shared/ConfirmDialog';
import { copyText } from '@/components/shared/CopyLink';
import { ApiErrorException } from '@/api/client';
import {
  cancelQualification, getCurrentQualification, getQualification, submitQualification,
  type PadOutcome, type QualificationView,
} from '@/api/endpoints/qualifications';
import { PAD } from '@/services/dialer/callPage/outcomes';
import { COMMON_TIME_ZONES, browserTimeZone, zonedToIso } from '@/services/dialer/callPage/zonedTime';
import { newCorrelationId } from '@/services/dialer/callPage/correlationLog';
import { correctionErrorAction, correctionErrorKey, manualFixes, windowClosed } from '@/services/dialer/qualificationCorrection';
import { invalidateProspectListAllPages } from '@/hooks/useProspectListBeyondCap';

/**
 * « Corriger la qualification » from the call history (W4, ADR-047 §4,
 * DES-17). Same server workflow as the call page: revision n+1 with
 * expected_revision, pending 3 s (« Annuler » while it counts down), then
 * committed; what the correction could not undo (a meeting, a CRM that
 * already received the old outcome) is said once it has committed.
 *
 * States: loading → ready | never (not qualified through the workflow) |
 * expired (24 h passed) | error; ready → saving → pending → done | cancelled.
 */
export interface CorrectableCall {
  id: string;
  prospect_name?: string | null;
}

type State =
  | { kind: 'loading' }
  | { kind: 'never' }
  | { kind: 'expired' }
  | { kind: 'ready'; current: QualificationView }
  | { kind: 'saving'; current: QualificationView }
  | { kind: 'pending'; view: QualificationView; previous: QualificationView }
  | { kind: 'done'; view: QualificationView; fixes: Array<'meeting_manual_fix' | 'crm_manual_fix'> }
  | { kind: 'cancelled'; current: QualificationView }
  | { kind: 'error'; code: string; correlationId?: string; current?: QualificationView };

/**
 * Everything a corrected outcome shows up in (REV-W4-F4): the history page
 * and the call page (`parallel-calls`), the engine's calls and queue
 * (`all-calls`, `prospect-queue`: dial eligibility, « Prêts à appeler »), the
 * list past the engine's cap, and the Performances aggregates.
 */
export const CORRECTION_QUERY_KEYS = [['parallel-calls'], ['all-calls'], ['prospect-queue'], ['dialer-analytics']] as const;

export function invalidateAfterCorrection(queryClient: QueryClient): void {
  for (const queryKey of CORRECTION_QUERY_KEYS) void queryClient.invalidateQueries({ queryKey: [...queryKey] });
  invalidateProspectListAllPages(queryClient);
}

/**
 * REV-W4-F5: the refresh after the commit runs from a timer that is NOT tied
 * to the dialog. Closing the dialog during the 3 s pending window used to
 * refresh right away, before the server committed, and never again: the
 * history kept the old outcome. Not cleared on unmount on purpose.
 */
export function refreshAfterCommit(queryClient: QueryClient, delayMs: number, callId: string, onCommitted?: (callId: string) => void): void {
  setTimeout(() => {
    invalidateAfterCorrection(queryClient);
    onCommitted?.(callId);
  }, Math.max(0, delayMs));
}

const codeOf = (err: unknown): string => (err instanceof ApiErrorException ? err.code : 'NETWORK');

interface Props {
  call: CorrectableCall | null;
  onOpenChange(open: boolean): void;
  /** Wait before reading the effects after commit_at (ms); tests shorten it. */
  settleMs?: number;
  /** Once the correction has committed (even if the dialog was closed): refresh what the caller shows of that call. */
  onCommitted?(callId: string): void;
}

export function CorrectQualificationDialog({ call, onOpenChange, settleMs = 1500, onCommitted }: Props) {
  const { t } = useTranslation(['dialer', 'common']);
  const queryClient = useQueryClient();
  const [ask, confirmDialog] = useConfirmDialog();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [outcome, setOutcome] = useState<PadOutcome | null>(null);
  const [note, setNote] = useState('');
  const [followDate, setFollowDate] = useState('');
  const [followTime, setFollowTime] = useState('');
  const [timezone, setTimezone] = useState(() => browserTimeZone());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callId = call?.id ?? null;

  const load = useCallback(async () => {
    if (!callId) return;
    setState({ kind: 'loading' });
    try {
      const res = await getCurrentQualification(callId);
      if (!res.current) setState({ kind: 'never' });
      else if (windowClosed(res.correctable_until)) setState({ kind: 'expired' });
      else { setState({ kind: 'ready', current: res.current }); setOutcome(null); }
    } catch (err) {
      setState({ kind: 'error', code: codeOf(err) });
    }
  }, [callId]);

  useEffect(() => {
    // REV-W4-F12: another call starts from a blank form, not the previous
    // call's note and callback date.
    setOutcome(null);
    setNote('');
    setFollowDate('');
    setFollowTime('');
    if (callId) void load();
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [callId, load]);

  const refresh = () => {
    invalidateAfterCorrection(queryClient);
    if (callId) onCommitted?.(callId);
  };

  const followUpAt = outcome === 'callback_requested' ? zonedToIso(followDate, followTime, timezone) : null;
  const canSave = state.kind === 'ready' && !!outcome && outcome !== state.current.outcome
    && (outcome !== 'callback_requested' || !!followUpAt);

  const save = async () => {
    if (state.kind !== 'ready' || !outcome || !callId) return;
    const current = state.current;
    if (outcome === 'do_not_call') {
      const ok = await ask({
        title: t('dialer:callPage.dnc.title'),
        description: t('dialer:callPage.dnc.description'),
        confirmLabel: t('dialer:callPage.dnc.confirm'),
      });
      if (!ok) return;
    }
    const qualificationId = newCorrelationId();
    setState({ kind: 'saving', current });
    try {
      const view = await submitQualification(callId, {
        qualification_id: qualificationId,
        outcome,
        expected_revision: current.revision,
        ...(note.trim() ? { note } : {}),
        ...(outcome === 'callback_requested' && followUpAt ? { follow_up_at: followUpAt, timezone } : {}),
      });
      setState({ kind: 'pending', view, previous: current });
      // Once committed, read what it could not undo (DES-17).
      const wait = Math.max(0, Date.parse(view.commit_at) - Date.now()) + settleMs;
      // The screens refresh after the commit even if the dialog is closed
      // meanwhile (REV-W4-F5); a cancelled correction refreshes for nothing.
      refreshAfterCommit(queryClient, wait, callId, onCommitted);
      timer.current = setTimeout(() => {
        void getQualification(view.qualification_id).then((after) => {
          if (after.state === 'cancelled') return;
          const fixes = manualFixes(after.effects);
          setState({ kind: 'done', view: after, fixes });
        }).catch(() => {
          // The correction stands; only the notice is unknown.
          setState({ kind: 'done', view, fixes: [] });
        });
      }, wait);
    } catch (err) {
      setState({ kind: 'error', code: codeOf(err), correlationId: qualificationId, current });
    }
  };

  const undo = async () => {
    if (state.kind !== 'pending') return;
    const { view, previous } = state;
    if (timer.current) clearTimeout(timer.current);
    try {
      await cancelQualification(view.qualification_id);
      setState({ kind: 'cancelled', current: previous });
      toast.info(t('dialer:callHistory.correct.cancelled'));
    } catch (err) {
      const code = codeOf(err);
      // Too late: it was applied; read its effects like a normal end.
      if (code === 'QUALIFICATION_NOT_PENDING') {
        toast.error(t('dialer:callHistory.correct.errors.QUALIFICATION_NOT_PENDING'));
        try {
          const after = await getQualification(view.qualification_id);
          setState({ kind: 'done', view: after, fixes: manualFixes(after.effects) });
        } catch { setState({ kind: 'done', view, fixes: [] }); }
        refresh();
        return;
      }
      setState({ kind: 'error', code, correlationId: view.qualification_id, current: previous });
    }
  };

  const close = () => {
    if (timer.current && state.kind === 'pending') {
      // Closing does not cancel: the server commits it anyway, and
      // refreshAfterCommit (scheduled at save) refreshes the screens then.
      clearTimeout(timer.current);
    }
    onOpenChange(false);
  };

  const name = call?.prospect_name || t('dialer:callHistory.detail.fallbackTitle');
  const outcomeLabel = (o: string) => t(`dialer:callPage.outcomes.${o}`, { defaultValue: o });

  return (
    <Dialog open={!!call} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent className="max-w-lg" data-testid="correct-qualification">
        <DialogHeader>
          <DialogTitle>{t('dialer:callHistory.correct.title')}</DialogTitle>
          <DialogDescription>{name}</DialogDescription>
        </DialogHeader>

        {state.kind === 'loading' && (
          <div role="status" aria-label={t('common:actions.loading')} className="space-y-2 py-2">
            <div className="h-4 w-1/2 animate-pulse rounded motion-reduce:animate-none" style={{ background: 'var(--vf-surface-3)' }} />
            <div className="h-24 animate-pulse rounded motion-reduce:animate-none" style={{ background: 'var(--vf-surface-3)' }} />
          </div>
        )}

        {state.kind === 'never' && (
          <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>{t('dialer:callHistory.correct.never')}</p>
        )}

        {state.kind === 'expired' && (
          <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>{t('dialer:callHistory.correct.errors.QUALIFICATION_CORRECTION_EXPIRED')}</p>
        )}

        {(state.kind === 'ready' || state.kind === 'saving') && (
          <form
            className="space-y-4"
            onSubmit={(e) => { e.preventDefault(); void save(); }}
          >
            <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>
              {t('dialer:callHistory.correct.current', { outcome: outcomeLabel(state.current.outcome), revision: state.current.revision })}
            </p>
            <div role="radiogroup" aria-label={t('dialer:callHistory.correct.newOutcome')} className="grid grid-cols-2 gap-2">
              {PAD.map((entry) => {
                const isCurrent = entry.outcome === state.current.outcome;
                const isSel = outcome === entry.outcome;
                return (
                  <button
                    key={entry.outcome}
                    type="button"
                    role="radio"
                    aria-checked={isSel}
                    disabled={isCurrent || state.kind === 'saving'}
                    onClick={() => setOutcome(entry.outcome)}
                    className="flex min-h-11 items-center gap-2 rounded-lg border px-2.5 text-left text-sm focus-visible:outline focus-visible:outline-2 disabled:opacity-50"
                    style={{
                      borderColor: isSel ? 'var(--vf-accent)' : 'var(--vf-border)',
                      background: isSel ? 'var(--vf-accent-bg)' : 'transparent',
                      color: 'var(--vf-text-1)',
                      outlineColor: 'var(--vf-accent)',
                    }}
                  >
                    <span className="flex-1">{outcomeLabel(entry.labelKey)}</span>
                    {isCurrent && <span className="text-[11px]" style={{ color: 'var(--vf-text-3)' }}>{t('dialer:callHistory.correct.currentTag')}</span>}
                  </button>
                );
              })}
            </div>

            {outcome === 'callback_requested' && (
              <fieldset className="grid grid-cols-3 gap-2">
                <legend className="mb-1 text-xs" style={{ color: 'var(--vf-text-2)' }}>{t('dialer:callPage.callback.required')}</legend>
                <label className="text-xs" style={{ color: 'var(--vf-text-2)' }}>
                  {t('dialer:callPage.callback.date')}
                  <input type="date" required value={followDate} onChange={(e) => setFollowDate(e.target.value)}
                    className="mt-1 h-11 w-full rounded-md border bg-transparent px-2 text-sm" style={{ borderColor: 'var(--vf-border)', color: 'var(--vf-text-1)' }} />
                </label>
                <label className="text-xs" style={{ color: 'var(--vf-text-2)' }}>
                  {t('dialer:callPage.callback.time')}
                  <input type="time" required value={followTime} onChange={(e) => setFollowTime(e.target.value)}
                    className="mt-1 h-11 w-full rounded-md border bg-transparent px-2 text-sm" style={{ borderColor: 'var(--vf-border)', color: 'var(--vf-text-1)' }} />
                </label>
                <label className="text-xs" style={{ color: 'var(--vf-text-2)' }}>
                  {t('dialer:callPage.callback.timezone')}
                  <select value={timezone} onChange={(e) => setTimezone(e.target.value)}
                    className="mt-1 h-11 w-full rounded-md border bg-transparent px-1 text-sm" style={{ borderColor: 'var(--vf-border)', color: 'var(--vf-text-1)' }}>
                    {[...new Set([timezone, ...COMMON_TIME_ZONES])].map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                </label>
              </fieldset>
            )}
            {outcome === 'meeting_booked' && (
              <p className="text-xs" style={{ color: 'var(--vf-text-2)' }}>{t('dialer:callHistory.correct.meetingHint')}</p>
            )}

            <label className="block text-xs" style={{ color: 'var(--vf-text-2)' }}>
              {t('dialer:callHistory.correct.note')}
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 min-h-20 text-sm" />
            </label>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={close}>{t('common:actions.close')}</Button>
              <Button type="submit" disabled={!canSave}>
                {state.kind === 'saving' ? t('dialer:callHistory.correct.saving') : t('dialer:callHistory.correct.save')}
              </Button>
            </div>
          </form>
        )}

        {state.kind === 'pending' && (
          <div role="status" className="flex items-center justify-between gap-3 rounded-lg border p-3" style={{ borderColor: 'var(--vf-border)' }}>
            <p className="text-sm" style={{ color: 'var(--vf-text-1)' }}>
              {t('dialer:callHistory.correct.pending', { outcome: outcomeLabel(state.view.outcome) })}
            </p>
            <Button type="button" variant="outline" size="sm" className="min-h-11" onClick={() => void undo()}>{t('dialer:callHistory.correct.undo')}</Button>
          </div>
        )}

        {state.kind === 'done' && (
          <div className="space-y-2">
            <p role="status" className="text-sm" style={{ color: 'var(--vf-text-1)' }}>
              {t('dialer:callHistory.correct.done', { outcome: outcomeLabel(state.view.outcome) })}
            </p>
            {state.fixes.map((f) => (
              <p key={f} role="alert" className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--vf-status-callback-fg)', background: 'var(--vf-yellow-bg)', color: 'var(--vf-text-1)' }}>
                {t(`dialer:callHistory.correct.fixes.${f}`)}
              </p>
            ))}
            <div className="flex justify-end"><Button type="button" onClick={close}>{t('common:actions.close')}</Button></div>
          </div>
        )}

        {state.kind === 'cancelled' && (
          <div className="space-y-2">
            <p role="status" className="text-sm" style={{ color: 'var(--vf-text-1)' }}>{t('dialer:callHistory.correct.cancelled')}</p>
            <div className="flex justify-end"><Button type="button" onClick={close}>{t('common:actions.close')}</Button></div>
          </div>
        )}

        {state.kind === 'error' && (
          <div role="alert" className="space-y-2">
            <p className="text-sm" style={{ color: 'var(--vf-text-1)' }}>{t(`dialer:callHistory.correct.errors.${correctionErrorKey(state.code)}`)}</p>
            <p className="flex items-center gap-2 font-mono text-[11px]" style={{ color: 'var(--vf-text-3)' }} data-testid="correct-error-code">
              <span>{[state.code, state.correlationId].filter(Boolean).join(' · ')}</span>
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded hover:bg-[var(--vf-surface-3)]"
                aria-label={t('dialer:callPage.errors.copy')}
                onClick={() => { void copyText([state.code, state.correlationId].filter(Boolean).join(' ')).then((ok) => { if (ok) toast.success(t('dialer:callPage.errors.copied')); }); }}
              >
                <Copy className="h-3.5 w-3.5" aria-hidden />
              </button>
            </p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={close}>{t('common:actions.close')}</Button>
              {correctionErrorAction(state.code) === 'reload' && (
                <Button type="button" onClick={() => void load()}>{t('dialer:callHistory.correct.reload')}</Button>
              )}
              {correctionErrorAction(state.code) === 'retry' && (
                <Button type="button" onClick={() => (state.current ? setState({ kind: 'ready', current: state.current }) : void load())}>{t('common:actions.retry')}</Button>
              )}
            </div>
          </div>
        )}
        {confirmDialog}
      </DialogContent>
    </Dialog>
  );
}
